import { useMemo } from "react";
import { Decimal, resolvePolicy } from "@normax-performance/calculation-engine";
import { formatQuantityLabel, formatStandardDisplay } from "@normax-performance/shared-format";

const policy = resolvePolicy("v1");

export interface NormSnapshotLite {
  standardQuantity: string;
  baseDurationMinutes: string;
}

export interface LinePreview {
  plannedQuantityLabel: string | null;
  performanceRatioLabel: string | null;
  deviationLabel: string | null;
  performanceRatioRaw: string | null;
}

/**
 * Local, instant calculation preview (DAILY_ENTRY_INTERACTION_SPEC.md §10) —
 * imports the SAME packages/calculation-engine v1 policy used authoritatively
 * on the backend (SYSTEM_ARCHITECTURE.md §1). This is never sent to the
 * server as a computed value; the server always re-derives it independently.
 */
export function useLinePreview(
  norm: NormSnapshotLite | null,
  durationMinutesRaw: string,
  reportedQuantityRaw: string,
): LinePreview {
  return useMemo(() => {
    const duration = Number(durationMinutesRaw);
    const reported = Number(reportedQuantityRaw);
    if (!norm || durationMinutesRaw.trim() === "" || reportedQuantityRaw.trim() === "" || Number.isNaN(duration) || Number.isNaN(reported)) {
      return {
        plannedQuantityLabel: null,
        performanceRatioLabel: null,
        deviationLabel: null,
        performanceRatioRaw: null,
      };
    }

    const result = policy.computeLine({
      standardQuantity: new Decimal(norm.standardQuantity),
      baseDurationMinutes: new Decimal(norm.baseDurationMinutes),
      durationMinutes: new Decimal(durationMinutesRaw),
      reportedQuantity: new Decimal(reportedQuantityRaw),
    });

    const display = formatStandardDisplay({
      performanceRatioPercent: policy.rounding.formatPercentage(result.performanceRatioRaw),
      deviationPercent: policy.rounding.formatPercentage(result.deviationRaw),
      normedMinutes: "0",
    });

    return {
      plannedQuantityLabel: formatQuantityLabel(policy.rounding.formatQuantity(result.plannedQuantityRaw)),
      performanceRatioLabel: display.performanceRatioLabel,
      deviationLabel: display.deviationLabel,
      performanceRatioRaw: result.performanceRatioRaw?.toString() ?? null,
    };
  }, [norm, durationMinutesRaw, reportedQuantityRaw]);
}
